import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { BookOpen, Plus, Trash2, X, Loader2, ShieldCheck, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface UserGuideline {
  id: string;
  content: string;
  status: string;
  guidelineScope?: "self" | "single_client" | "all_assigned_clients";
  targetAgencyClientId?: string | null;
  targetClientName?: string | null;
  scopeLabel?: string;
  createdAt: string;
  ruleDescription?: string;
  parsedRule?: { type: string };
}

interface ClientOption {
  id: string;
  name: string;
}

interface ClientAccountMapping {
  clientOptions: ClientOption[];
}

type GuidelineScope = "self" | "single_client" | "all_assigned_clients";

const MAX_CONTENT_LENGTH = 1000;

export function MonitoringGuidelinesPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [guidelineScope, setGuidelineScope] = useState<GuidelineScope>("self");
  const [targetClientId, setTargetClientId] = useState("");

  const { data: guidelines = [], isLoading } = useQuery<UserGuideline[]>({
    queryKey: ["/api/monitoring/guidelines"],
  });

  const { data: clientMapping } = useQuery<ClientAccountMapping>({
    queryKey: ["/api/monitoring/client-accounts"],
  });

  const clientOptions = clientMapping?.clientOptions ?? [];
  const hasClientOptions = clientOptions.length > 0;

  function resetFormState() {
    setNewContent("");
    setGuidelineScope(hasClientOptions ? "all_assigned_clients" : "self");
    setTargetClientId("");
    setIsAdding(false);
  }

  const activeGuidelines = guidelines.filter((g) => g.status === "active");

  const addMutation = useMutation({
    mutationFn: (payload: {
      content: string;
      guidelineScope: GuidelineScope;
      targetAgencyClientId: string | null;
    }) => apiRequest("POST", "/api/monitoring/guidelines", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/monitoring/guidelines"],
      });
      resetFormState();
      toast({ title: t("monitoringGuidelines.toast.added") });
    },
    onError: (error: { message?: string }) => {
      toast({
        title: error.message || t("monitoringGuidelines.toast.addError"),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/monitoring/guidelines/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/monitoring/guidelines"],
      });
      toast({ title: t("monitoringGuidelines.toast.deleted") });
    },
    onError: () => {
      toast({ title: t("monitoringGuidelines.toast.tryAgain"), variant: "destructive" });
    },
  });

  function handleSubmit() {
    const trimmed = newContent.trim();
    if (!trimmed) return;

    if (guidelineScope === "single_client" && !targetClientId) {
      toast({ title: t("monitoringGuidelines.toast.pickClient"), variant: "destructive" });
      return;
    }

    addMutation.mutate({
      content: trimmed,
      guidelineScope,
      targetAgencyClientId: guidelineScope === "single_client" ? targetClientId : null,
    });
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-medium text-white/80">{t("monitoringGuidelines.title")}</h3>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30">
            {t("monitoringGuidelines.beta")}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setIsAdding(true);
            setGuidelineScope(hasClientOptions ? "all_assigned_clients" : "self");
            setTargetClientId("");
          }}
          disabled={isAdding}
          className="h-7 text-xs border-white/10 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white"
        >
          <Plus className="w-3 h-3 mr-1" />
          {t("monitoringGuidelines.addButton")}
        </Button>
      </div>

      {/* Description */}
      <div className="px-4 py-2 text-[11px] text-white/30">{t("monitoringGuidelines.desc")}</div>

      {/* Add form */}
      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 space-y-2">
              {hasClientOptions && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setGuidelineScope("all_assigned_clients");
                      setTargetClientId("");
                    }}
                    className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                      guidelineScope === "all_assigned_clients"
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                        : "border-white/10 bg-white/5 text-white/60 hover:border-white/20 hover:text-white/80"
                    }`}
                  >
                    {t("monitoringGuidelines.scope.allClients")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setGuidelineScope("single_client")}
                    className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                      guidelineScope === "single_client"
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                        : "border-white/10 bg-white/5 text-white/60 hover:border-white/20 hover:text-white/80"
                    }`}
                  >
                    {t("monitoringGuidelines.scope.singleClient")}
                  </button>
                </div>
              )}

              {!hasClientOptions && (
                <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/50">
                  {t("monitoringGuidelines.scope.selfNote")}
                </div>
              )}

              {guidelineScope === "single_client" && (
                <select
                  value={targetClientId}
                  onChange={(e) => setTargetClientId(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500/30"
                >
                  <option value="" className="bg-[#0b0f16] text-white/80">
                    {t("monitoringGuidelines.selectClient")}
                  </option>
                  {clientOptions.map((client) => (
                    <option
                      key={client.id}
                      value={client.id}
                      className="bg-[#0b0f16] text-white/80"
                    >
                      {client.name}
                    </option>
                  ))}
                </select>
              )}

              <div className="relative">
                <textarea
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value.slice(0, MAX_CONTENT_LENGTH))}
                  placeholder={t("monitoringGuidelines.placeholder")}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500/30 resize-none min-h-[80px]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      handleSubmit();
                    }
                  }}
                />
                <span className="absolute bottom-2 right-3 text-[10px] text-white/20">
                  {newContent.length}/{MAX_CONTENT_LENGTH}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!newContent.trim() || addMutation.isPending}
                  className="h-7 text-xs bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border-0"
                >
                  {addMutation.isPending ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-3 h-3 mr-1" />
                  )}
                  {t("monitoringGuidelines.save")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFormState}
                  className="h-7 text-xs text-white/30 hover:text-white/60"
                >
                  {t("monitoringGuidelines.cancel")}
                </Button>
              </div>
              <p className="text-[10px] text-white/20">{t("monitoringGuidelines.verifyNote")}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guidelines list */}
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-white/20" />
        </div>
      )}

      {!isLoading && activeGuidelines.length === 0 && !isAdding && (
        <div className="px-4 py-6 text-center">
          <p className="text-xs text-white/25">{t("monitoringGuidelines.empty")}</p>
        </div>
      )}

      {activeGuidelines.length > 0 && (
        <div className="px-4 pb-3 space-y-1.5">
          <AnimatePresence>
            {activeGuidelines.map((guideline) => (
              <motion.div
                key={guideline.id}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.15 }}
                className="group flex items-start gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:border-white/10 transition-colors"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400/60 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/60 leading-relaxed">{guideline.content}</p>
                  {guideline.scopeLabel && (
                    <p className="mt-1 text-[10px] text-amber-400/60">
                      {t("monitoringGuidelines.scopeLabel")}: {guideline.scopeLabel}
                      {guideline.targetClientName ? ` (${guideline.targetClientName})` : ""}
                    </p>
                  )}
                  {guideline.ruleDescription && (
                    <p
                      className={`mt-1 text-[10px] flex items-center gap-1 ${
                        guideline.parsedRule?.type === "context_only"
                          ? "text-amber-400/50"
                          : "text-emerald-400/50"
                      }`}
                    >
                      <Info className="w-2.5 h-2.5 shrink-0" />
                      {guideline.ruleDescription}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => deleteMutation.mutate(guideline.id)}
                  disabled={deleteMutation.isPending}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all shrink-0"
                  title={t("monitoringGuidelines.deleteTooltip")}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
